import { Observable, throwError, timer } from 'rxjs';
import { map, catchError, take, timeout, filter } from 'rxjs/operators';

export const normalizePhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) {
    return phoneNumber;
  }

  return phoneNumber.replace('+', '').replace(/ /g, '');
};

export const normalizeInstagram = (login: string): string => {
  if (!login) {
    return login;
  }

  login
    .replace('https://www.instagram.com/', '')
    .replace('www.instagram.com/', '')
    .replace('instagram.com/', '')
    .replace('@', '');
  return login;
};

export const normalizeTelegram = (login: string): string => {
  if (!login) {
    return login;
  }

  login.replace('https://t.me/', '').replace('www.t.me/', '').replace('t.me/', '').replace('@', '');
  return login;
};

export const normalizeDomain = (domain: string): string => {
  if (!domain) {
    return domain;
  }

  return domain.replace('https://', '').replace('www.', '').replace('http://', '').replace('/', '');
};

export const normalizeLink = (link: string): string => {
  if (!link) {
    return link;
  }

  if (/^https?:\/\//i.test(link)) {
    return link;
  } else {
    return 'https://' + link;
  }
};

export function isValidInstagram(input: string): boolean {
  // Regular expression for Instagram URL
  const urlPattern =
    /^(?:https?:\/\/)?(?:www\.)?instagram\.com\/([a-zA-Z0-9_](?:(?:[a-zA-Z0-9_]|(?:\.(?!\.))){0,28}(?:[a-zA-Z0-9_]))?)$/;

  // Regular expression for Instagram nickname or @nickname
  const nicknamePattern = /^@?([a-zA-Z0-9_](?:(?:[a-zA-Z0-9_]|(?:\.(?!\.))){0,28}(?:[a-zA-Z0-9_]))?)$/;

  return urlPattern.test(input) || nicknamePattern.test(input);

  // Usage examples
  // console.log(isValidInstagram('https://www.instagram.com/username')); // true
  // console.log(isValidInstagram('instagram.com/username')); // true
  // console.log(isValidInstagram('@username')); // true
  // console.log(isValidInstagram('username')); // true
  // console.log(isValidInstagram('invalid_username!')); // false
}

function normalizeRuPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return '+7' + digits;
  }

  if ((digits.length === 11 && digits.startsWith('7')) || digits.startsWith('8')) {
    return '+7' + digits.substr(1);
  }

  return phone;
}

export function isValidRuPhone(phone: string): boolean {
  if (!phone) {
    return false;
  }

  const normalized = normalizeRuPhone(phone);
  return /^\+7\d{10}$/.test(normalized);
}

export function isValidAnyCountryPhone(phone: string): boolean {
  if (!phone) {
    return false;
  }

  const normalized = normalizeAnyCountryPhone(phone);
  return /^\+\d{1,3}\d{10,}$/.test(normalized);
}

function normalizeAnyCountryPhone(phone: string): string {
  if (!phone) {
    return phone;
  }

  return '+' + phone.replace(/\D/g, '');
}

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export function replaceAll(str: string, find: string, replace: string) {
  return str.replace(new RegExp(escapeRegExp(find), 'g'), replace);
}

export function preloadImages(urls: string[]) {
  return urls.map(url => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  });
}

function moveItemInArray(array: any[], from: number, to: number) {
  if (to === from) {
    return array;
  }

  const target = array[from];
  const increment = to < from ? -1 : 1;

  for (let k = from; k !== to; k += increment) {
    array[k] = array[k + increment];
  }

  array[to] = target;
  return array;
}

export type AccentuateType = 'scale-up' | 'scale-up-little' | 'shine';
export const accentuateElement = ({
  elem,
  animationDuration = 2000,
  type = 'scale-up',
}: {
  elem: HTMLElement;
  animationDuration?: number;
  type: AccentuateType;
}) => {
  elem.classList.remove(`accentuate-${type}`);
  elem.style.animationDuration = animationDuration + 'ms';
  setTimeout(() => {
    elem.classList.add(`accentuate-${type}`);
    clearTimeout((elem as any)['accentuateTimeout']);
    (elem as any)['accentuateTimeout'] = setTimeout(() => {
      elem.classList.remove(`accentuate-${type}`);
    }, animationDuration);
  }, 100);
};

export function genPassword(): string {
  const min = 1000;
  const max = 9999;
  return '' + (Math.floor(Math.random() * (max - min + 1)) + min);
}

export function plural(n: number, [d1, d2, d5]: [string, string, string], prependValue = false, strong: boolean = false): string {
  const originalN = n;
  const word = ((): string | null => {
    const abs = Math.abs(n);
    const mod100 = abs % 100;
    const mod10 = abs % 10;

    if (mod100 >= 11 && mod100 <= 14) {
      return d5;
    }

    if (mod10 === 1) {
      return d1;
    }

    if (mod10 >= 2 && mod10 <= 4) {
      return d2;
    }

    return d5;
  })();

  const prepend = prependValue ? originalN + ' ' : '';

  return strong ? `<strong>${prepend}${word}</strong>` : `${prepend}${word}`;
}

export function isValidHttpUrl(string: string): boolean {
  let url: URL;
  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
}

export function isValidUrl(url: string): boolean {
  if (!url) {
    return false;
  }

  url = normalizeLink(url);
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ); // fragment locator
  return !!pattern.test(url);
}

export function safeStringify(value: any): string {
  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value);
    // eslint-disable-next-line unused-imports/no-unused-vars
  } catch (e) {
    return '[can`t stringify]';
  }
}

export function elementInViewport({
  el,
  container = window,
  threshold = 0,
  full = false,
}: {
  el: HTMLElement;
  container?: Window | HTMLElement;
  threshold?: number;
  full?: boolean;
}): boolean {
  if (!el) {
    return false;
  }

  const rect = el.getBoundingClientRect();
  const containerRect =
    container === window
      ? { top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth }
      : (container as HTMLElement).getBoundingClientRect();

  if (full) {
    // Проверяем, полностью ли элемент находится в видимой области контейнера
    return (
      rect.top >= containerRect.top &&
      rect.left >= containerRect.left &&
      rect.bottom <= containerRect.bottom &&
      rect.right <= containerRect.right
    );
  }

  // Проверяем, находится ли хотя бы часть элемента в видимой области контейнера
  return (
    rect.top < containerRect.bottom - threshold &&
    rect.left < containerRect.right - threshold &&
    rect.bottom > containerRect.top + threshold &&
    rect.right > containerRect.left + threshold
  );
}

export function elementsInViewport(els: HTMLElement[], threshold: number = 0): boolean {
  return els.some(el => elementInViewport({ el, threshold }));
}

export function minmax(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

const b64abc = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
  'a',
  'b',
  'c',
  'd',
  'e',
  'f',
  'g',
  'h',
  'i',
  'j',
  'k',
  'l',
  'm',
  'n',
  'o',
  'p',
  'q',
  'r',
  's',
  't',
  'u',
  'v',
  'w',
  'x',
  'y',
  'z',
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '+',
  '/',
];

function bytesToBase64(bytes: Uint8Array): string {
  let res = '',
    i;
  const l = bytes.length;
  for (i = 2; i < l; i += 3) {
    res += b64abc[bytes[i - 2] >> 2];
    res += b64abc[((bytes[i - 2] & 0x03) << 4) | (bytes[i - 1] >> 4)];
    res += b64abc[((bytes[i - 1] & 0x0f) << 2) | (bytes[i] >> 6)];
    res += b64abc[bytes[i] & 0x3f];
  }

  if (i === l + 1) {
    // 1 octet yet to write
    res += b64abc[bytes[i - 2] >> 2];
    res += b64abc[(bytes[i - 2] & 0x03) << 4];
    res += '==';
  }

  if (i === l) {
    // 2 octets yet to write
    res += b64abc[bytes[i - 2] >> 2];
    res += b64abc[((bytes[i - 2] & 0x03) << 4) | (bytes[i - 1] >> 4)];
    res += b64abc[(bytes[i - 1] & 0x0f) << 2];
    res += '=';
  }

  return res;
}

export function createPath(path: string, rootObject: { [key: string]: any }): void {
  const pathParts = path.split('.');
  for (let i = 1; i < pathParts.length; i++) {
    const part = pathParts[i];
    rootObject[part] = rootObject[part] || {};
    rootObject = rootObject[part];
  }
}

export function isHTMLElementsIntersects(div1: HTMLElement, div2: HTMLElement): boolean {
  // Calculate the bounds of each div
  const bounds1 = div1.getBoundingClientRect();
  const bounds2 = div2.getBoundingClientRect();
  // Check if the bounds of the two divs intersect
  return (
    bounds1.right > bounds2.left && bounds1.left < bounds2.right && bounds1.bottom > bounds2.top && bounds1.top < bounds2.bottom
  );
}

export function deleteQueryParamFromUrl(key: string): void {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete(key);
  const newParams = searchParams.toString();
  window.history.replaceState({}, '', `${window.location.pathname}${newParams ? '?' + newParams : ''}`);
}

export function queryParam(name: string): string {
  const match = RegExp('[?&]' + name + '=([^&]*)').exec(window.location.search);
  return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : '';
}

class QueryParam {
  name: string;
  value: string;

  constructor(name: string, value: string) {
    this.name = name;
    this.value = value;
  }

  attach(): void {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set(this.name, this.value);
    const newParams = searchParams.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${newParams ? '?' + newParams : ''}`);
  }

  detach(): void {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete(this.name);
    const newParams = searchParams.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${newParams ? '?' + newParams : ''}`);
  }
}

export function getAllQueryParams(): { [p: string]: QueryParam } {
  const searchParams = new URLSearchParams(window.location.search);
  const params: { [key: string]: QueryParam } = {};
  searchParams.forEach(function (value, key) {
    params[key] = new QueryParam(key, value);
  });

  return params;
}

export function sleep(duration: number): Promise<unknown> {
  return new Promise(resolve => setTimeout(resolve, duration));
}

export function isValidDomain(domain: string): boolean {
  const regex = /^(?!:\/\/)([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])\.([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])$/;

  return regex.test(domain);
}

export function assignDefaults<T extends object>(obj: T, props: Partial<T>): T {
  if (!props) {
    return obj;
  }

  if (!obj) {
    obj = {} as T;
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  Object.entries(props).forEach(([key, value]) => {
    // @ts-expect-error dynamic key assignment
    obj[key] = obj[key] ?? value;
  });
  return obj;
}

// Promise-based version
export function waitForProperty<T extends object, K extends keyof T>(
  obj: T,
  propName: K,
  maxWaitTime: number = 5000,
): Promise<T[K]> {
  return new Promise<T[K]>((resolve, reject) => {
    const startTime = Date.now();

    const checkProp = (): void => {
      // eslint-disable-next-line no-prototype-builtins
      if (obj.hasOwnProperty(propName)) {
        resolve(obj[propName]);
      } else if (Date.now() - startTime >= maxWaitTime) {
        reject(new Error(`Timeout: Property ${String(propName)} not found within ${maxWaitTime}ms`));
      } else {
        setTimeout(checkProp, 100);
      }
    };

    checkProp();
  });
}

// Corrected RxJS version
export function waitForPropertyRx<T extends object, K extends keyof T>(
  obj: T,
  propName: K,
  maxWaitTime: number = 5_000,
): Observable<T[K]> {
  return timer(0, 100).pipe(
    // eslint-disable-next-line no-prototype-builtins
    map(() => (obj.hasOwnProperty(propName) ? obj[propName] : undefined)),
    filter((value): value is T[K] => value !== undefined),
    take(1),
    timeout(maxWaitTime),
    catchError(error => {
      if (error.name === 'TimeoutError') {
        return throwError(() => new Error(`Timeout: Property ${String(propName)} not found within ${maxWaitTime}ms`));
      }

      return throwError(() => error);
    }),
  );
}

export function generateColorForAnyString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = hash % 360;
  return `hsl(${hue}, 100%, 93%)`;
}

export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize));
  }

  return result;
}

export async function runConcurrently<T>(
  tasks: (() => Promise<T>)[],
  maxConcurrent: number,
  initialDelay: number = 0,
): Promise<T[]> {
  const results: T[] = [];
  const running: Promise<void>[] = [];

  async function runTask(task: () => Promise<T>, index: number): Promise<void> {
    const result = await task();
    results[index] = result;
  }

  async function spawnTask(delay: number = 0): Promise<void> {
    if (tasks.length === 0) return;
    const task = tasks.shift()!;
    const index = results.length;
    results.push(undefined as any);

    await new Promise(resolve => setTimeout(resolve, delay));

    const promise = runTask(task, index).then(() => {
      running.splice(running.indexOf(promise), 1);

      return spawnTask();
    });
    running.push(promise);
  }

  // Запускаем начальные задачи с заданным смещением
  for (let i = 0; i < Math.min(maxConcurrent, tasks.length); i++) {
    await spawnTask(i * initialDelay);
  }

  await Promise.all(running);

  return results;
}

export function getOrientation(sizes: { width: number; height: number }): 's' | 'h' | 'v' | null {
  if (!sizes) {
    return null;
  }

  const eps = (sizes.height / 100) * 5;
  if (Math.abs(sizes.width - sizes.height) < eps) {
    return 's';
  }

  if (sizes.width > sizes.height) {
    return 'h';
  }

  return 'v';
}

export function watchVariable<T extends object>(
  obj: T,
  onChange?: (property: string | symbol, oldValue: any, newValue: any, stack?: string) => void,
): T {
  return new Proxy(obj, {
    set(target, property, value, receiver) {
      const oldValue = Reflect.get(target, property, receiver);
      const result = Reflect.set(target, property, value, receiver);
      // Получаем стек вызовов, чтобы понять, где произошло изменение
      const stack = new Error().stack;
      // eslint-disable-next-line no-empty
      if (property === 'width' && value === 500) {
      }

      console.log(`Property "${String(property)}" changed`, oldValue, '->', value);
      onChange?.(property, oldValue, value, stack);
      return result;
    },
  });
}

export function assignNonNullValues(target: any, source: any) {
  for (const key in source) {
    if (source[key] !== null && source[key] !== undefined && target[key] !== source[key]) {
      target[key] = source[key];
    }
  }

  return target;
}
